import { API_BASE_URL, API_ENDPOINTS } from '../../config/api';

export function authHeaders(json = false) {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  if (json) headers['Content-Type'] = 'application/json';
  return headers;
}

export function coverUrl(path) {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  const token = localStorage.getItem('token');
  const base = `${API_BASE_URL}${path}`;
  return token ? `${base}?token=${encodeURIComponent(token)}` : base;
}

export async function guidesFetch(path, options = {}) {
  const res = await fetch(`${API_ENDPOINTS.GUIDES}${path}`, options);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.message || 'Request failed');
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

/** Load program taxonomy categories; seeds defaults when empty (admins only). */
export async function loadGuideCategories() {
  let list = await guidesFetch('/categories', { headers: authHeaders() });
  list = Array.isArray(list) ? list : [];
  if (list.length === 0) {
    try {
      const seeded = await guidesFetch('/categories/seed-defaults', {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify({ onlyIfEmpty: true })
      });
      list = seeded.categories || [];
    } catch {
      // non-admins may not seed; leave empty
    }
  }
  return list;
}

export const GUIDE_TITLE_PLACEHOLDER = 'Type your header here';
export const GUIDE_SUBTOPIC_CONTENT_PLACEHOLDER = 'Add your subtopic content here';
export const GUIDE_LOWER_SUBTOPIC_CONTENT_PLACEHOLDER = 'Add your lower subtopic content here';
export const GUIDE_TOPIC_CONTENT_PLACEHOLDER = 'Add your topic content here';
export const GUIDE_TITLE_PLACEHOLDER_ID = 'guide-title-placeholder';
export const GUIDE_TITLE_ATTR = 'data-guides-title';

function clampHeadingLevel(n, fallback = 1) {
  const num = Number(n);
  if (!Number.isInteger(num) || num < 1 || num > 6) return fallback;
  return num;
}

function headingNumber(tagOrLevel) {
  return typeof tagOrLevel === 'number'
    ? tagOrLevel
    : Number(String(tagOrLevel).replace(/^h/i, ''));
}

/**
 * Outline level relative to the selected main-topic heading N:
 * Topic = N, Subtopic = N+1, Third-level = N+2 (H1–H6 only).
 */
export function outlineLevelFromTag(tagOrLevel, topicHeadingLevel = 1) {
  const n = headingNumber(tagOrLevel);
  if (!Number.isInteger(n) || n < 1 || n > 6) return 0;
  const N = clampHeadingLevel(topicHeadingLevel, 1);
  return Math.max(1, n - N + 1);
}

export function defaultTagForOutlineLevel(outlineLevel, topicHeadingLevel = 1) {
  const N = clampHeadingLevel(topicHeadingLevel, 1);
  const heading = N + Number(outlineLevel) - 1;
  if (heading < 1 || heading > 6) return null;
  return `h${heading}`;
}

export function canInsertOutlineLevel(outlineLevel, topicHeadingLevel = 1) {
  return Boolean(defaultTagForOutlineLevel(outlineLevel, topicHeadingLevel));
}

export function isGuideTitleHeading(el) {
  if (!el || el.nodeType !== 1 || !/^H[1-6]$/.test(el.tagName)) return false;
  if (el.getAttribute(GUIDE_TITLE_ATTR) === '1') return true;
  return el.id === GUIDE_TITLE_PLACEHOLDER_ID;
}

export function inferTopicHeadingLevel(headings = []) {
  let min = null;
  headings.forEach((h) => {
    if (h?.isTitle) return;
    const n = Number(h?.level);
    if (n >= 1 && n <= 6 && (min == null || n < min)) min = n;
  });
  return min || 1;
}

function inferTopicHeadingLevelFromRoot(root) {
  if (!root?.querySelectorAll) return 1;
  let min = null;
  root.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach((el) => {
    if (isGuideTitleHeading(el)) return;
    const n = Number(el.tagName.substring(1));
    if (n >= 1 && n <= 6 && (min == null || n < min)) min = n;
  });
  return min || 1;
}

function slugify(text) {
  return String(text)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40) || 'section';
}

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function blockText(el) {
  return (el?.textContent || '').replace(/\u00a0/g, ' ').trim();
}

function isVisuallyEmptyBlock(el) {
  if (!el) return true;
  const text = blockText(el);
  if (text) return false;
  const meaningful = el.querySelector?.('img, table, iframe, video, hr');
  return !meaningful;
}

function markAsTitleHeading(el) {
  if (!el) return;
  el.id = GUIDE_TITLE_PLACEHOLDER_ID;
  el.setAttribute(GUIDE_TITLE_ATTR, '1');
}

function findTitleElement(doc, fallbackTitle = '', { allowLegacy = false } = {}) {
  const marked = Array.from(doc.body.querySelectorAll('h1, h2, h3, h4, h5, h6'))
    .find((el) => isGuideTitleHeading(el));
  if (marked) return marked;
  if (!allowLegacy) return null;

  const first = doc.body.querySelector('h1, h2, h3, h4, h5, h6');
  if (!first) return null;
  const text = blockText(first);
  const fb = String(fallbackTitle || '').trim();
  if (fb && text === fb) return first;
  // Legacy documents used the first H1/H2 as the locked document title.
  if (/^H[12]$/.test(first.tagName)) return first;
  return null;
}

export function emptyGuideTitleHtml(level = 1) {
  const n = clampHeadingLevel(level, 1);
  return (
    `<h${n} id="${GUIDE_TITLE_PLACEHOLDER_ID}" ${GUIDE_TITLE_ATTR}="1"` +
    ` data-guides-placeholder="${escapeHtml(GUIDE_TITLE_PLACEHOLDER)}"><br></h${n}>`
  );
}

export function serializeGuideTitleHtml({ level = 1, innerHtml = '', text = '' } = {}) {
  const n = clampHeadingLevel(level, 1);
  const trimmedText = String(text || '').replace(/\u00a0/g, ' ').trim();
  const inner = String(innerHtml || '').trim();
  const empty = !trimmedText && !inner.replace(/<br\s*\/?>/gi, '').replace(/&nbsp;/gi, '').trim();
  if (empty) return emptyGuideTitleHtml(n);
  const content = inner || escapeHtml(trimmedText);
  return (
    `<h${n} id="${GUIDE_TITLE_PLACEHOLDER_ID}" ${GUIDE_TITLE_ATTR}="1">${content}</h${n}>`
  );
}

export function parseGuideTitleHtml(html) {
  try {
    const doc = new DOMParser().parseFromString(html || '', 'text/html');
    const el = doc.body.querySelector('h1, h2, h3, h4, h5, h6') || doc.body.firstElementChild;
    if (!el || !/^H[1-6]$/.test(el.tagName)) {
      const text = blockText(doc.body);
      return {
        level: 1,
        innerHtml: text ? escapeHtml(text) : '<br>',
        text,
        empty: !text
      };
    }
    const text = blockText(el);
    const innerHtml = el.innerHTML.trim() || '<br>';
    return {
      level: clampHeadingLevel(Number(el.tagName.substring(1)), 1),
      innerHtml,
      text,
      empty: !text
    };
  } catch {
    return { level: 1, innerHtml: '<br>', text: '', empty: true };
  }
}

export function normalizeGuideTitleHtml(html, { fallbackTitle = '', level } = {}) {
  const parsed = parseGuideTitleHtml(html);
  const n = clampHeadingLevel(level ?? parsed.level, parsed.level || 1);
  if (parsed.empty && fallbackTitle) {
    return serializeGuideTitleHtml({ level: n, text: fallbackTitle });
  }
  return serializeGuideTitleHtml({
    level: n,
    innerHtml: parsed.innerHtml,
    text: parsed.text
  });
}

/**
 * Split a stored document into an isolated title heading and body HTML.
 * Title heading level never participates in topic/subtopic classification.
 */
export function splitGuideDocumentHtml(html, { fallbackTitle = '' } = {}) {
  const fallback = String(fallbackTitle || '').trim();
  if (!html) {
    return {
      titleHtml: fallback
        ? serializeGuideTitleHtml({ level: 1, text: fallback })
        : emptyGuideTitleHtml(1),
      titleText: fallback,
      titleLevel: 1,
      bodyHtml: '<p></p>'
    };
  }
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const titleEl = findTitleElement(doc, fallback, { allowLegacy: true });
    if (titleEl) {
      markAsTitleHeading(titleEl);
      if (isVisuallyEmptyBlock(titleEl) && fallback) {
        titleEl.textContent = fallback;
        titleEl.removeAttribute('data-guides-placeholder');
      } else if (isVisuallyEmptyBlock(titleEl)) {
        titleEl.setAttribute('data-guides-placeholder', GUIDE_TITLE_PLACEHOLDER);
        if (!titleEl.innerHTML.trim()) titleEl.innerHTML = '<br>';
      } else {
        titleEl.removeAttribute('data-guides-placeholder');
      }
      const titleLevel = clampHeadingLevel(Number(titleEl.tagName.substring(1)), 1);
      const titleText = blockText(titleEl);
      const titleHtml = titleEl.outerHTML;
      titleEl.remove();
      while (doc.body.firstElementChild) {
        const el = doc.body.firstElementChild;
        if (!/^H[1-6]$/.test(el.tagName) || isGuideTitleHeading(el)) break;
        if (!isVisuallyEmptyBlock(el)) break;
        const p = doc.createElement('p');
        p.innerHTML = el.innerHTML?.trim() ? el.innerHTML : '<br>';
        doc.body.replaceChild(p, el);
      }
      const bodyHtml = doc.body.innerHTML.trim() || '<p></p>';
      return { titleHtml, titleText, titleLevel, bodyHtml };
    }
    return {
      titleHtml: fallback
        ? serializeGuideTitleHtml({ level: 1, text: fallback })
        : emptyGuideTitleHtml(1),
      titleText: fallback,
      titleLevel: 1,
      bodyHtml: html
    };
  } catch {
    return {
      titleHtml: fallback
        ? serializeGuideTitleHtml({ level: 1, text: fallback })
        : emptyGuideTitleHtml(1),
      titleText: fallback,
      titleLevel: 1,
      bodyHtml: html || '<p></p>'
    };
  }
}

export function composeGuideDocumentHtml(titleHtml, bodyHtml) {
  const title = normalizeGuideTitleHtml(titleHtml);
  let body = bodyHtml || '<p></p>';
  try {
    const doc = new DOMParser().parseFromString(body, 'text/html');
    Array.from(doc.body.querySelectorAll('h1, h2, h3, h4, h5, h6'))
      .filter((el) => isGuideTitleHeading(el))
      .forEach((el) => el.remove());
    body = doc.body.innerHTML.trim() || '<p></p>';
  } catch {
    /* keep body */
  }
  return `${title}${body}`;
}

function assignHeadingIds(doc, { isolateTitle = false, fallbackTitle = '' } = {}) {
  const titleEl = isolateTitle
    ? findTitleElement(doc, fallbackTitle, { allowLegacy: true })
    : Array.from(doc.body.querySelectorAll('h1, h2, h3, h4, h5, h6')).find((el) => isGuideTitleHeading(el));
  if (titleEl) markAsTitleHeading(titleEl);

  const all = [];
  const nodes = doc.body.querySelectorAll('h1, h2, h3, h4, h5, h6');
  nodes.forEach((el, index) => {
    let id = el.getAttribute('id');
    if (!id) {
      id = `heading-${index}-${slugify(el.textContent || 'section')}`;
      el.setAttribute('id', id);
    }
    const level = Number(el.tagName.substring(1));
    const isTitle = titleEl ? el === titleEl : isGuideTitleHeading(el);
    const text = blockText(el);
    all.push({
      id,
      text: text || (isTitle ? GUIDE_TITLE_PLACEHOLDER : `Heading ${index + 1}`),
      level,
      empty: !text,
      isTitle
    });
  });

  const bodyHeadings = all.filter((h) => !h.isTitle);
  const topicHeadingLevel = inferTopicHeadingLevel(bodyHeadings);
  bodyHeadings.forEach((h) => {
    h.outlineLevel = outlineLevelFromTag(h.level, topicHeadingLevel);
    h.topicHeadingLevel = topicHeadingLevel;
  });
  const titleHeading = all.find((h) => h.isTitle) || null;
  if (titleHeading) {
    titleHeading.outlineLevel = 0;
    titleHeading.topicHeadingLevel = topicHeadingLevel;
  }
  return { headings: bodyHeadings, titleHeading, topicHeadingLevel };
}

/** Extract body headings for TOC; title heading is excluded from topic hierarchy. */
export function extractHeadingsFromHtml(html, { isolateTitle = false, fallbackTitle = '' } = {}) {
  if (!html) {
    return {
      headings: [],
      titleHeading: null,
      topicHeadingLevel: 1,
      htmlWithIds: html || ''
    };
  }
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const { headings, titleHeading, topicHeadingLevel } = assignHeadingIds(doc, {
      isolateTitle,
      fallbackTitle
    });
    return { headings, titleHeading, topicHeadingLevel, htmlWithIds: doc.body.innerHTML };
  } catch {
    return {
      headings: [],
      titleHeading: null,
      topicHeadingLevel: 1,
      htmlWithIds: html
    };
  }
}

/**
 * Put outline ids on the live headings. Setting the attribute does not
 * replace the document, so the caret stays where the user is typing.
 * Returns true when an id changed.
 */
export function stampGuideHeadingIds(root, headings) {
  if (!root?.querySelectorAll || !headings?.length) return false;
  const nodes = [...root.querySelectorAll('h1, h2, h3, h4, h5, h6')].filter(
    (el) => !isGuideTitleHeading(el)
  );
  let changed = false;
  headings.forEach((heading, index) => {
    const el = nodes[index];
    const id = heading?.id;
    if (!el || !id || el.id === id) return;
    el.id = id;
    changed = true;
  });
  return changed;
}

/** Isolated document title text (not a body topic heading). */
export function getTopicTitleFromHtml(html, fallbackTitle = '') {
  return splitGuideDocumentHtml(html, { fallbackTitle }).titleText;
}

export function hasTopicTitleHeading(html) {
  if (!html) return false;
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return Boolean(findTitleElement(doc, '', { allowLegacy: true }));
  } catch {
    return false;
  }
}

/** Keep title isolated at the start of stored HTML. */
export function ensureTopicTitleInHtml(html, title) {
  const split = splitGuideDocumentHtml(html, { fallbackTitle: title });
  const titleHtml = String(title || '').trim()
    ? serializeGuideTitleHtml({
      level: split.titleLevel,
      innerHtml: parseGuideTitleHtml(split.titleHtml).empty
        ? escapeHtml(title)
        : parseGuideTitleHtml(split.titleHtml).innerHtml,
      text: title
    })
    : split.titleHtml;
  return composeGuideDocumentHtml(titleHtml, split.bodyHtml);
}

export function ensureTitlePlaceholderInHtml(html) {
  const split = splitGuideDocumentHtml(html);
  return composeGuideDocumentHtml(split.titleHtml, split.bodyHtml);
}

/**
 * Once a block or its subtopic section has real text, drop placeholder attrs
 * so new empty paragraphs under the same subtitle do not show them again.
 * Mutates the live editor DOM in place (avoids controlled-value remount / cursor jump).
 */
export function stripGuidePlaceholdersInPlace(root) {
  if (!root?.querySelectorAll || !root.querySelector?.('[data-guides-placeholder]')) return;

  root.querySelectorAll('[data-guides-placeholder]').forEach((el) => {
    if (!isVisuallyEmptyBlock(el)) el.removeAttribute('data-guides-placeholder');
  });

  const topicHeadingLevel = inferTopicHeadingLevelFromRoot(root);
  Array.from(root.querySelectorAll('h1, h2, h3, h4, h5, h6')).forEach((heading) => {
    if (isGuideTitleHeading(heading)) return;
    const outline = outlineLevelFromTag(heading.tagName, topicHeadingLevel);
    if (outline < 1) return;

    const sectionNodes = [];
    let hasBodyContent = false;
    let node = heading.nextSibling;
    while (node) {
      if (node.nodeType === 1) {
        const tag = node.tagName?.toLowerCase?.() || '';
        if (/^h[1-6]$/.test(tag) && outlineLevelFromTag(tag, topicHeadingLevel) <= outline) break;
        sectionNodes.push(node);
        if (!/^h[1-6]$/.test(tag) && !isVisuallyEmptyBlock(node)) {
          hasBodyContent = true;
        }
      } else if (node.nodeType === 3 && String(node.textContent || '').trim()) {
        hasBodyContent = true;
      }
      node = node.nextSibling;
    }

    if (!hasBodyContent) return;
    sectionNodes.forEach((n) => {
      if (n.nodeType !== 1) return;
      if (n.hasAttribute?.('data-guides-placeholder')) {
        n.removeAttribute('data-guides-placeholder');
      }
      n.querySelectorAll?.('[data-guides-placeholder]').forEach((el) => {
        el.removeAttribute('data-guides-placeholder');
      });
    });
  });
}

/**
 * String-html variant (e.g. programmatic edits). Prefer stripGuidePlaceholdersInPlace while typing.
 */
export function clearGuidePlaceholdersAfterContent(html) {
  if (!html || !html.includes('data-guides-placeholder')) return html || '';
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const before = doc.body.innerHTML;
    stripGuidePlaceholdersInPlace(doc.body);
    const after = doc.body.innerHTML;
    return after === before ? html : after;
  } catch {
    return html;
  }
}

function isGuideBibliography(node) {
  if (!node || node.nodeType !== 1) return false;
  if (node.getAttribute('data-rte-generated') === 'bibliography') return true;
  return node.classList?.contains('rte-bibliography');
}

function isSectionBoundary(node, startOutline, topicHeadingLevel) {
  if (!node || node.nodeType !== 1) return false;
  if (isGuideBibliography(node)) return true;
  const tagName = node.tagName?.toLowerCase?.() || '';
  return (
    /^h[1-6]$/.test(tagName)
    && !isGuideTitleHeading(node)
    && outlineLevelFromTag(tagName, topicHeadingLevel) <= startOutline
  );
}

function pinGuideBibliography(parent) {
  if (!parent) return;
  const bib = [...parent.childNodes].find((node) => isGuideBibliography(node));
  if (bib) parent.appendChild(bib);
}

/** Heading plus every following node until the next same-or-higher outline heading. */
function collectSectionNodes(startEl, topicHeadingLevel) {
  const startOutline = outlineLevelFromTag(startEl.tagName, topicHeadingLevel);
  const nodes = [startEl];
  let node = startEl.nextSibling;
  while (node && !isSectionBoundary(node, startOutline, topicHeadingLevel)) {
    nodes.push(node);
    node = node.nextSibling;
  }
  return nodes;
}

function outlineParentHeading(heading, topicHeadingLevel) {
  const outline = outlineLevelFromTag(heading.tagName, topicHeadingLevel);
  let node = heading.previousSibling;
  while (node) {
    if (
      node.nodeType === 1
      && /^H[1-6]$/.test(node.tagName)
      && !isGuideTitleHeading(node)
      && outlineLevelFromTag(node.tagName, topicHeadingLevel) < outline
    ) {
      return node;
    }
    node = node.previousSibling;
  }
  return null;
}

/** Find the node before which to insert to place content at the end of a section. */
function findSectionEndInsertBefore(startEl, topicHeadingLevel = 1) {
  const nodes = collectSectionNodes(startEl, topicHeadingLevel);
  return nodes[nodes.length - 1].nextSibling;
}

function createPlaceholderParagraph(doc, placeholderText) {
  const para = doc.createElement('p');
  para.setAttribute('data-guides-placeholder', placeholderText);
  para.innerHTML = '<br>';
  return para;
}

/**
 * Insert a topic / subtopic / third-level heading using the document's
 * dynamic hierarchy (relative to the selected topic heading level N).
 */
export function insertGuideSection(html, {
  text,
  outlineLevel = 2,
  afterHeadingId = null,
  topicHeadingLevel
} = {}) {
  const trimmed = String(text || '').trim();
  if (!trimmed) return { html: html || '', id: null };
  const doc = new DOMParser().parseFromString(html || '', 'text/html');
  assignHeadingIds(doc);
  const N = clampHeadingLevel(topicHeadingLevel ?? inferTopicHeadingLevelFromRoot(doc.body), 1);
  const level = outlineLevel === 1 || outlineLevel === 3 ? outlineLevel : 2;
  const tag = defaultTagForOutlineLevel(level, N);
  if (!tag) return { html: html || '', id: null };
  const placeholderText = level === 3
    ? GUIDE_LOWER_SUBTOPIC_CONTENT_PLACEHOLDER
    : level === 1
      ? GUIDE_TOPIC_CONTENT_PLACEHOLDER
      : GUIDE_SUBTOPIC_CONTENT_PLACEHOLDER;

  const id = `heading-${Date.now()}-${slugify(trimmed)}`;
  const heading = doc.createElement(tag);
  heading.id = id;
  heading.textContent = trimmed;
  const para = createPlaceholderParagraph(doc, placeholderText);

  let insertBefore = null;
  if (afterHeadingId) {
    const start = doc.body.querySelector(`[id="${CSS.escape(afterHeadingId)}"]`);
    if (start && /^H[1-6]$/.test(start.tagName) && !isGuideTitleHeading(start)) {
      insertBefore = findSectionEndInsertBefore(start, N);
      if (!insertBefore) {
        doc.body.appendChild(heading);
        doc.body.appendChild(para);
        pinGuideBibliography(doc.body);
        return { html: doc.body.innerHTML, id };
      }
    }
  }

  if (insertBefore) {
    insertBefore.before(heading);
    heading.after(para);
  } else {
    doc.body.appendChild(heading);
    doc.body.appendChild(para);
  }
  pinGuideBibliography(doc.body);
  return { html: doc.body.innerHTML, id };
}

/** Remove a heading and all content until the next same-or-higher outline level. */
export function removeGuideSection(html, headingId, topicHeadingLevel) {
  if (!html || !headingId) return html || '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  assignHeadingIds(doc);
  const start = doc.body.querySelector(`[id="${CSS.escape(headingId)}"]`);
  if (!start || !/^H[1-6]$/.test(start.tagName)) return doc.body.innerHTML;
  if (isGuideTitleHeading(start)) return doc.body.innerHTML;

  const N = clampHeadingLevel(topicHeadingLevel ?? inferTopicHeadingLevelFromRoot(doc.body), 1);
  collectSectionNodes(start, N).forEach((n) => n.parentNode?.removeChild(n));
  pinGuideBibliography(doc.body);
  return doc.body.innerHTML;
}

/**
 * Move a topic, subtopic, or third-level section onto a sibling at the same
 * outline level. The heading and everything under it (paragraphs, images,
 * tables, nested headings) move together.
 * Dragging downward places the section after the target section.
 * Dragging upward places it before the target heading.
 */
export function moveGuideSection(html, sourceHeadingId, targetHeadingId, topicHeadingLevel) {
  if (!html || !sourceHeadingId || !targetHeadingId || sourceHeadingId === targetHeadingId) {
    return html || '';
  }
  const doc = new DOMParser().parseFromString(html, 'text/html');
  assignHeadingIds(doc);
  const N = clampHeadingLevel(topicHeadingLevel ?? inferTopicHeadingLevelFromRoot(doc.body), 1);
  const escapeId = (value) => (
    typeof CSS !== 'undefined' && CSS.escape
      ? CSS.escape(value)
      : String(value).replace(/"/g, '\\"')
  );
  const source = doc.body.querySelector(`[id="${escapeId(sourceHeadingId)}"]`);
  const target = doc.body.querySelector(`[id="${escapeId(targetHeadingId)}"]`);
  if (!source || !target || source === target) return html;
  if (!/^H[1-6]$/.test(source.tagName) || !/^H[1-6]$/.test(target.tagName)) return html;
  if (isGuideTitleHeading(source) || isGuideTitleHeading(target)) return html;
  if (source.parentNode !== target.parentNode) return html;

  const sourceOutline = outlineLevelFromTag(source.tagName, N);
  const targetOutline = outlineLevelFromTag(target.tagName, N);
  if (!sourceOutline || sourceOutline !== targetOutline) return html;
  if (outlineParentHeading(source, N) !== outlineParentHeading(target, N)) return html;

  const following = typeof Node !== 'undefined' ? Node.DOCUMENT_POSITION_FOLLOWING : 4;
  const placeAfter = Boolean(source.compareDocumentPosition(target) & following);
  const nodes = collectSectionNodes(source, N);
  if (nodes.includes(target)) return html;

  const parent = source.parentNode;
  const fragment = doc.createDocumentFragment();
  nodes.forEach((node) => fragment.appendChild(node));
  if (placeAfter) {
    const insertBefore = findSectionEndInsertBefore(target, N);
    if (insertBefore) parent.insertBefore(fragment, insertBefore);
    else parent.appendChild(fragment);
  } else {
    parent.insertBefore(fragment, target);
  }
  pinGuideBibliography(parent);
  return doc.body.innerHTML;
}

export function flattenNotebookTopics(notebook) {
  const sections = (notebook?.sections || []).slice().sort((a, b) => a.sortOrder - b.sortOrder);
  const list = [];
  sections.forEach((section) => {
    const topics = (section.topics || []).slice().sort((a, b) => a.sortOrder - b.sortOrder);
    topics.forEach((topic) => {
      list.push({
        ...topic,
        sectionId: section.id,
        sectionTitle: section.title
      });
    });
  });
  return list;
}

export function formatGuideDate(value) {
  if (!value) return '';
  try {
    return new Date(value).toISOString().slice(0, 10);
  } catch {
    return '';
  }
}

export function formatGuideLongDate(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleDateString(undefined, {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  } catch {
    return '';
  }
}

export const GUIDE_ACCENT_COLORS = [
  'var(--blue)',
  'var(--indigo)',
  'var(--green)',
  'var(--orange)',
  'var(--red)',
  'var(--cyan)',
  'var(--yellow)'
];

function seedNumber(seed) {
  const n = Number.isFinite(seed)
    ? seed
    : String(seed || '')
        .split('')
        .reduce((a, c) => a + c.charCodeAt(0), 0);
  return Math.abs(n);
}

export function getGuideAccent(seed) {
  return GUIDE_ACCENT_COLORS[seedNumber(seed) % GUIDE_ACCENT_COLORS.length];
}

/** Same vibrant placeholder swatches used on Discussions cards. */
export function getPlaceholderSwatch(seed) {
  const swatches = [
    { bg: '#FF5733', fg: '#FFFFFF' },
    { bg: '#00D084', fg: '#FFFFFF' },
    { bg: '#2196F3', fg: '#FFFFFF' },
    { bg: '#FFB300', fg: '#FFFFFF' },
    { bg: '#D72660', fg: '#FFFFFF' },
    { bg: '#9400D3', fg: '#FFFFFF' },
    { bg: '#FF0080', fg: '#FFFFFF' }
  ];
  const n = Number.isFinite(seed)
    ? seed
    : String(seed || '')
        .split('')
        .reduce((a, c) => a + c.charCodeAt(0), 0);
  return swatches[Math.abs(n) % swatches.length];
}

export function truncateGuideTitle(title, maxChars = 60) {
  const t = (title || '').trim();
  if (t.length <= maxChars) return t;
  return `${t.slice(0, maxChars)}…`;
}

export const GUIDE_LIST_TITLES = {
  mine: 'My Notebooks',
  discover: 'Discover',
  shared: 'Shared with Me'
};
